import { LoaderFunction } from 'react-router-dom';

// GraphQL
import { client } from '../../../../gql/client';
import { FETCH_ACCIDENTS } from '../../../../gql/queries/accidents/accidents';
import { FETCH_FILTER_ACCIDENTS } from '../../../../gql/queries/accidents/predefinedData';
import { GET_CONCESSIONS } from '../../../../gql/queries/orders/orders';
import {
  FETCH_CAPITOLS,
  FETCH_SUB_CAPITOLS,
  FETCH_CLASSIFICATIONS,
  FETCH_PREDEFINED_ORDERS,
} from '../../../../gql/queries/orders/predefinedData';
import { FETCH_TEAMS, FETCH_USERS } from '../../../../gql/queries/users';
import { selectAccidentOrders } from '../../../../gql/selectors/accidents';

// Types
import { AuthContextType } from '../../../../hooks/useAuth';
import {
  tableIds,
  takeBase,
} from '../../../../components/display/tables/types';
import { firstPageTake } from '../../../../utils/cursor-take.helper';

// Redux
import { store } from '../../../../state/configureStore';
import { getCursor } from '../../../../state/selectors/ui/tables';
import { loadingData } from '../../../../state/actions/ui/loadingData';
import { setCursor } from '../../../../state/actions/ui/cursorTables';
import { refreshOrderFlags } from '../../../../gql/services/orderFlags';

export const HistoricalAccidentsReportLoader: (
  userData: AuthContextType | null
) => LoaderFunction = (userData) => async () => {
  // El trabajo entero vive aquí dentro, íntegro. Solo ha dejado de
  // esperarse: la promesa tiene que llegar PENDIENTE al `<Await>` para que
  // el router termine la navegación al instante y el `<Fallback>` enseñe el
  // Loader DENTRO de la pantalla nueva.
  const load = async () => {
  // Banderitas (spec 2026-10-22 § 7.2): en paralelo y sin esperar; nunca lanza.
  void refreshOrderFlags(userData);
  try {
    if (!userData?.user) return {};

    const cursorAccidents = getCursor(tableIds.REPORTING_ACCIDENTS)(
      store.getState()
    );
    const dispatch = store.dispatch;
    const roleActive = userData.user.user.rolActive;

    // Fix A4: Pre-set concessionId when there's only 1 concession to avoid Apollo cache partition splits
    let effectiveConcessionId = cursorAccidents?.concessionId;
    if (!effectiveConcessionId) {
      try {
        const { data: { concessions } } = await client.query({ query: GET_CONCESSIONS });
        if (concessions?.length === 1) {
          effectiveConcessionId = concessions[0].id;
          dispatch(setCursor(tableIds.REPORTING_ACCIDENTS, { ...cursorAccidents, concessionId: effectiveConcessionId }));
        }
      } catch { /* continue without concessionId */ }
    }

    // FIX (loader-loading-flag-leak): cierra cualquier flag fugado por un run
    // anterior interrumpido.
    dispatch(loadingData(tableIds.REPORTING_ACCIDENTS, false));

    const readAccidents = client.readQuery({
      query: FETCH_ACCIDENTS,
      variables: {
        input: {
          take: cursorAccidents?.take,
          cursor: cursorAccidents?.cursor,
          concessionId: effectiveConcessionId,
          weatherId: cursorAccidents?.weatherId,
          mediumKnowledgeId: cursorAccidents?.mediumKnowledgeId,
          orderBy: cursorAccidents?.orderBy,
          sortField: cursorAccidents?.sortField,
          toDate: cursorAccidents?.toDate,
          fromDate: cursorAccidents?.fromDate,
          indexType: cursorAccidents?.indexType,
          indexExpedition: cursorAccidents?.indexExpedition,
          classification: cursorAccidents?.classification,
          predefinedOrderId: cursorAccidents?.predefinedOrderId,
          capitolId: cursorAccidents?.capitolId,
          subCapitolId: cursorAccidents?.subCapitolId,
          states: cursorAccidents?.states,
          userIds: cursorAccidents?.userIds,
          teamIds: cursorAccidents?.teamIds,
          pkInitKm: cursorAccidents?.pkInitKm,
          pkInitMeter: cursorAccidents?.pkInitMeter,
          pkEndKm: cursorAccidents?.pkEndKm,
          pkEndMeter: cursorAccidents?.pkEndMeter,
          description: cursorAccidents?.description,
          laneCutting: cursorAccidents?.laneCutting,
          // «Solo marcadas» (spec 2026-10-22 § 8.3): true o ausente.
          onlyFlagged: cursorAccidents?.onlyFlagged,
        },
      },
    });

    const pageInfo = readAccidents?.accidents?.pageInfo;
    const totalCount = readAccidents?.accidents?.totalCount;

    const [
      { data: { concessions } },
      { data: { weathers, mediumKnowledges } },
      { data: { classifications } },
      { data: { capitols } },
      { data: { subCapitols } },
      { data: { predefinedOrders } },
      { data: { users } },
      { data: { teams } },
    ] = await Promise.all([
      client.query({ query: GET_CONCESSIONS }),
      client.query({ query: FETCH_FILTER_ACCIDENTS }),
      client.query({ query: FETCH_CLASSIFICATIONS }),
      client.query({ query: FETCH_CAPITOLS, fetchPolicy: 'cache-first' }),
      client.query({ query: FETCH_SUB_CAPITOLS, fetchPolicy: 'cache-first' }),
      client.query({ query: FETCH_PREDEFINED_ORDERS, fetchPolicy: 'cache-first' }),
      client.query({ query: FETCH_USERS, fetchPolicy: 'cache-first' }),
      client.query({ query: FETCH_TEAMS, fetchPolicy: 'cache-first' }),
    ]);

    const filters = {
      mediumKnowledges,
      weathers,
      concessions,
      classifications,
      capitols,
      subCapitols,
      predefinedOrders,
      users,
      teams,
    };

    const isCacheEmpty = JSON.stringify(readAccidents) === '{}';

    const accidents =
      readAccidents &&
      !isCacheEmpty &&
      (await selectAccidentOrders(readAccidents?.accidents?.accidents));

    const combinedData = {
      accidents,
      totalCount,
      filters,
      pageInfo,
      roleActive,
    };

    if (!readAccidents || isCacheEmpty) {
      dispatch(loadingData(tableIds.REPORTING_ACCIDENTS, true));
      try {
      let data;
      if (isCacheEmpty) {
        // Tarea #11: respetar el take guardado en cursor (page-size selector).
        // Espejo de HistoricalActionsReportLoader.
        const effectiveTake = firstPageTake(cursorAccidents?.take, takeBase);
        data = await client.query({
          query: FETCH_ACCIDENTS,
          variables: {
            input: {
              take: effectiveTake,
              cursor: '',
              concessionId: effectiveConcessionId,
              weatherId: cursorAccidents?.weatherId,
              mediumKnowledgeId: cursorAccidents?.mediumKnowledgeId,
              sortField: cursorAccidents?.sortField,
              orderBy: cursorAccidents?.orderBy ?? 'desc',
              toDate: cursorAccidents?.toDate,
              fromDate: cursorAccidents?.fromDate,
              indexType: cursorAccidents?.indexType,
              indexExpedition: cursorAccidents?.indexExpedition,
              classification: cursorAccidents?.classification,
              predefinedOrderId: cursorAccidents?.predefinedOrderId,
              capitolId: cursorAccidents?.capitolId,
              subCapitolId: cursorAccidents?.subCapitolId,
              states: cursorAccidents?.states,
              userIds: cursorAccidents?.userIds,
              teamIds: cursorAccidents?.teamIds,
              pkInitKm: cursorAccidents?.pkInitKm,
              pkInitMeter: cursorAccidents?.pkInitMeter,
              pkEndKm: cursorAccidents?.pkEndKm,
              pkEndMeter: cursorAccidents?.pkEndMeter,
              description: cursorAccidents?.description,
              laneCutting: cursorAccidents?.laneCutting,
              onlyFlagged: cursorAccidents?.onlyFlagged,
            },
          },
        });

        // setCursorData OMITE sortField/orderBy → el merge de cursorTables los preserva
        const setCursorData = {
          take: effectiveTake,
          cursor: '',
          concessionId: effectiveConcessionId,
          weatherId: cursorAccidents?.weatherId,
          mediumKnowledgeId: cursorAccidents?.mediumKnowledgeId,
          toDate: cursorAccidents?.toDate,
          fromDate: cursorAccidents?.fromDate,
          indexType: cursorAccidents?.indexType,
          indexExpedition: cursorAccidents?.indexExpedition,
          classification: cursorAccidents?.classification,
          predefinedOrderId: cursorAccidents?.predefinedOrderId,
          capitolId: cursorAccidents?.capitolId,
          subCapitolId: cursorAccidents?.subCapitolId,
          states: cursorAccidents?.states,
          userIds: cursorAccidents?.userIds,
          teamIds: cursorAccidents?.teamIds,
          pkInitKm: cursorAccidents?.pkInitKm,
          pkInitMeter: cursorAccidents?.pkInitMeter,
          pkEndKm: cursorAccidents?.pkEndKm,
          pkEndMeter: cursorAccidents?.pkEndMeter,
          description: cursorAccidents?.description,
          laneCutting: cursorAccidents?.laneCutting,
          onlyFlagged: cursorAccidents?.onlyFlagged,
        };

        dispatch(setCursor(tableIds.REPORTING_ACCIDENTS, setCursorData));
      } else {
        data = await client.query({
          query: FETCH_ACCIDENTS,
          variables: {
            input: {
              take: cursorAccidents?.take,
              cursor: cursorAccidents?.cursor,
              concessionId: effectiveConcessionId,
              weatherId: cursorAccidents?.weatherId,
              mediumKnowledgeId: cursorAccidents?.mediumKnowledgeId,
              orderBy: cursorAccidents?.orderBy ?? 'desc',
              sortField: cursorAccidents?.sortField,
              toDate: cursorAccidents?.toDate,
              fromDate: cursorAccidents?.fromDate,
              indexType: cursorAccidents?.indexType,
              indexExpedition: cursorAccidents?.indexExpedition,
              classification: cursorAccidents?.classification,
              predefinedOrderId: cursorAccidents?.predefinedOrderId,
              capitolId: cursorAccidents?.capitolId,
              subCapitolId: cursorAccidents?.subCapitolId,
              states: cursorAccidents?.states,
              userIds: cursorAccidents?.userIds,
              teamIds: cursorAccidents?.teamIds,
              pkInitKm: cursorAccidents?.pkInitKm,
              pkInitMeter: cursorAccidents?.pkInitMeter,
              pkEndKm: cursorAccidents?.pkEndKm,
              pkEndMeter: cursorAccidents?.pkEndMeter,
              description: cursorAccidents?.description,
              laneCutting: cursorAccidents?.laneCutting,
              onlyFlagged: cursorAccidents?.onlyFlagged,
            },
          },
          fetchPolicy: 'network-only',
        });
      }

      const accidentsResult = await selectAccidentOrders(data.data.accidents.accidents);

      const combinedDataNetwork = {
        accidents: accidentsResult,
        totalCount: data.data.accidents.totalCount,
        filters,
        pageInfo: data.data.accidents.pageInfo,
        roleActive,
      };

      return combinedDataNetwork;
      } finally {
        // FIX (loader-loading-flag-leak): cierra el flag aunque MISS lance.
        dispatch(loadingData(tableIds.REPORTING_ACCIDENTS, false));
      }
    }

    return combinedData;
  } catch (err) {
    console.log('Loader offline fallback:', err);
    // FIX (loader-loading-flag-leak): defensa para errores antes del MISS branch.
    store.dispatch(loadingData(tableIds.REPORTING_ACCIDENTS, false));
    return { accidents: [], totalCount: 0, filters: { mediumKnowledges: [], weathers: [], concessions: [], classifications: [], capitols: [], subCapitols: [], predefinedOrders: [], users: [], teams: [] }, pageInfo: { hasNextPage: false, hasPreviousPage: false }, roleActive: userData?.user?.user?.rolActive };
  }
};

  return { data: load() };
};
